/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useMemo } from 'react';
import { useWBL } from '../../context/WBLContext';
import { 
  Users, Shield, Search, ChevronDown, Key, UserCheck, 
  Check, X, Edit2, Crown, Eye
} from 'lucide-react';
import { isUserOrgLead } from '../../utils/orgTeamUtils';
import { StudentAvatar } from '../Common/StudentAvatar';
import { HeroBanner } from '../Common/HeroBanner';
import { PasswordManagementModal } from '../Common/PasswordManagementModal';
import { UserProfileModal } from './UserProfileModal';
import { UserProfile } from '../../types';
import { formatLastNameFirst, sortStudentsByLastName } from '../../utils/studentUtils';
import { useInAppDialog } from '../Common/InAppDialog';

export const SuperadminUsersPage: React.FC = () => {
  const { 
    users, 
    tenants, 
    updateUserProfile, 
    schools, 
    setUserAsOrgLead, 
    syncExistingOrgUsersToLeads,
    startProxySession,
    activeUser
  } = useWBL();
  const { showAlert, showToast } = useInAppDialog();
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [regionFilter, setRegionFilter] = useState<string>('all');
  
  const [savingUid, setSavingUid] = useState<string | null>(null);
  const [selectedPasswordUser, setSelectedPasswordUser] = useState<UserProfile | null>(null);
  const [selectedProfileUser, setSelectedProfileUser] = useState<UserProfile | null>(null);

  const toggleSchoolAssociation = async (user: UserProfile, targetSchoolId: string) => {
    setSavingUid(user.uid);
    try {
      const currentSchools = user.schoolsServedIds || [];
      const primarySchoolId = user.schoolId;
      
      if (primarySchoolId === targetSchoolId) {
        if (currentSchools.length > 0) {
          const nextPrimaryId = currentSchools[0];
          const nextSchoolsServedList = currentSchools.filter(id => id !== nextPrimaryId);
          const newPrimarySchool = schools.find(s => s.id === nextPrimaryId);
          
          await updateUserProfile(user.uid, {
            schoolId: nextPrimaryId,
            school: newPrimarySchool?.name || '',
            schoolsServedIds: nextSchoolsServedList,
          });
        } else {
          await updateUserProfile(user.uid, {
            schoolId: '',
            school: '',
            schoolsServedIds: [],
          });
        }
      } else if (currentSchools.includes(targetSchoolId)) {
        const nextSchoolsServedList = currentSchools.filter(id => id !== targetSchoolId);
        await updateUserProfile(user.uid, { 
          schoolsServedIds: nextSchoolsServedList,
        });
      } else {
        if (!primarySchoolId) {
          const newPrimarySchool = schools.find(s => s.id === targetSchoolId);
          await updateUserProfile(user.uid, {
            schoolId: targetSchoolId,
            school: newPrimarySchool?.name || ''
          });
        } else {
          const nextSchoolsServedList = [...currentSchools, targetSchoolId];
          await updateUserProfile(user.uid, { 
            schoolsServedIds: nextSchoolsServedList,
          });
        }
      }
    } catch (err) {
      console.error("Failed to update school association:", err);
    } finally {
      setSavingUid(null);
    }
  };

  const filteredUsers = useMemo(() => {
    const list = (users || []).filter(u => {
      const nameMatch = (u.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                        (`${u.contactFirstName || ''} ${u.contactLastName || ''}`).toLowerCase().includes(searchQuery.toLowerCase());
      const emailMatch = (u.email || '').toLowerCase().includes(searchQuery.toLowerCase());
      const matchesSearch = nameMatch || emailMatch;

      const matchesRole = roleFilter === 'all' || u.role === roleFilter;
      const userRegions = [u.tenantId, ...(u.extendedRegions || [])].filter(Boolean);
      const matchesRegion = regionFilter === 'all' || userRegions.includes(regionFilter);

      return matchesSearch && matchesRole && matchesRegion;
    });

    return sortStudentsByLastName(list, (u: UserProfile) => u.name, (u: UserProfile) => u);
  }, [users, searchQuery, roleFilter, regionFilter]);

  const toggleRegionAssociation = async (user: UserProfile, targetTenantId: string) => {
    setSavingUid(user.uid);
    try {
      const currentRegions = user.extendedRegions || [user.tenantId];
      let nextRegions: string[];
      
      if (currentRegions.includes(targetTenantId)) {
        if (user.tenantId === targetTenantId) {
          const remaining = currentRegions.filter(id => id !== targetTenantId);
          if (remaining.length > 0) {
            nextRegions = remaining;
            await updateUserProfile(user.uid, { 
              tenantId: remaining[0], 
              extendedRegions: remaining 
            });
          } else {
            showAlert({
              title: "Region Required",
              message: "A user must be assigned to at least one active region.",
              type: "warning"
            });
            return;
          }
        } else {
          nextRegions = currentRegions.filter(id => id !== targetTenantId);
          await updateUserProfile(user.uid, { extendedRegions: nextRegions });
        }
      } else {
        nextRegions = [...currentRegions, targetTenantId];
        await updateUserProfile(user.uid, { extendedRegions: nextRegions });
      }
    } catch (err) {
      console.error("Failed to update user regions:", err);
    } finally {
      setSavingUid(null);
    }
  };

  const getRoleBadgeClasses = (role: string) => {
    switch (role) {
      case 'advisor': return 'bg-purple-50 text-purple-700 border-purple-200';
      case 'organization': return 'bg-emerald-50 text-emerald-700 border-emerald-200';
      case 'student': return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'parent': return 'bg-amber-50 text-amber-700 border-amber-200';
      case 'regional_admin': return 'bg-rose-50 text-rose-700 border-rose-200';
      case 'superadmin': return 'bg-red-50 text-red-700 border-red-200';
      default: return 'bg-slate-50 text-slate-700 border-slate-200';
    }
  };

  return (
    <div className="space-y-6 font-sans animate-fade-in">
      <HeroBanner 
        title="GLOBAL USER DIRECTORY"
        subtitle={`MANAGING ${users.length} TOTAL USERS ACROSS THE NETWORK`}
        pageKey="registry"
      />

      <div className="w-[95%] mx-auto space-y-8 pb-12">
                <div className="bg-white rounded-3xl border border-slate-200 shadow-lg overflow-hidden flex flex-col">
          <div className="p-6 border-b border-slate-100 bg-slate-50/50 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <h2 className="text-lg font-black text-[#011d3c] tracking-tight uppercase flex items-center space-x-2">
              <Shield className="w-5 h-5 text-[#0094c8]" />
              <span>Global Authority Ledger</span>
            </h2>

            <div className="flex flex-wrap items-center gap-3">
              <div className="relative min-w-[240px]">
                <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search by name, email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full bg-white/50 border border-slate-200 outline-none rounded-xl py-2 pl-9 pr-4 focus:ring-1 focus:ring-custom-primary font-medium text-xs"
                />
              </div>

              <div className="relative">
                <select
                  value={roleFilter}
                  onChange={(e) => setRoleFilter(e.target.value)}
                  className="px-3 py-2 pr-8 border border-slate-200 rounded-xl text-xs bg-white text-slate-700 outline-none focus:ring-2 focus:ring-[#0094c8] appearance-none font-bold uppercase tracking-wider"
                >
                  <option value="all">ALL ROLES</option>
                  <option value="student">STUDENTS</option>
                  <option value="advisor">ADVISORS</option>
                  <option value="organization">EMPLOYERS</option>
                  <option value="parent">PARENTS</option>
                  <option value="regional_admin">REGIONAL ADMINS</option>
                  <option value="superadmin">SUPERADMINS</option>
                </select>
                <ChevronDown className="absolute right-2.5 top-3 w-3 h-3 text-slate-400 pointer-events-none" />
              </div>

              <div className="relative">
                <select
                  value={regionFilter}
                  onChange={(e) => setRegionFilter(e.target.value)}
                  className="px-3 py-2 pr-8 border border-slate-200 rounded-xl text-xs bg-white text-slate-700 outline-none focus:ring-2 focus:ring-[#0094c8] appearance-none font-bold uppercase tracking-wider"
                >
                  <option value="all">ALL REGIONS</option>
                  {tenants.map(t => (
                    <option key={t.id} value={t.id}>{(t.name || '').toUpperCase()}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-2.5 top-3 w-3 h-3 text-slate-400 pointer-events-none" />
              </div>

              <button
                type="button"
                onClick={async () => {
                  const updatedCount = await syncExistingOrgUsersToLeads();
                  showToast({
                    title: "Organization Leads Synced",
                    message: updatedCount > 0
                      ? `Updated ${updatedCount} existing organization user(s) to Organization Lead.`
                      : "All existing organization users are already synced with Lead status.",
                    type: "success"
                  });
                }}
                className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-amber-50 hover:bg-amber-100 border border-amber-300 text-amber-800 text-[10px] font-black uppercase tracking-wider cursor-pointer transition-all"
              >
                <Crown className="w-3.5 h-3.5 text-amber-600" />
                <span>Sync Existing Org Leads</span>
              </button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50/50 text-[10px] font-black uppercase text-slate-400 border-b border-slate-100 tracking-wider">
                  <th className="py-4 px-6">User Identity</th>
                  <th className="py-4 px-6">System Role</th>
                  <th className="py-4 px-6">Affiliation / Schools</th>
                  <th className="py-4 px-6">Regions</th>
                  <th className="py-4 px-6 text-right">Security</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-xs text-slate-700">
                {filteredUsers.map(user => (
                  <tr key={user.uid} className="hover:bg-slate-50/50 transition-colors">
                    <td className="py-4 px-6">
                      <div className="flex items-center space-x-3">
                        <div className="w-9 h-9 rounded-full overflow-hidden shrink-0 border border-slate-100 bg-slate-50">
                          <StudentAvatar studentId={user.uid} studentName={user.name} studentEmail={user.email} profile={user} />
                        </div>
                        <div>
                          <button
                            type="button"
                            onClick={() => setSelectedProfileUser(user)}
                            className="font-extrabold text-slate-800 text-sm hover:text-[#0094c8] hover:underline cursor-pointer text-left block"
                          >
                            {formatLastNameFirst(user.name, user)}
                          </button>
                          <p className="text-[10px] text-slate-400 mt-0.5">{user.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <span className={`px-2.5 py-0.5 rounded-full border text-[9px] font-black uppercase ${getRoleBadgeClasses(user.role)}`}>
                        {user.role}
                      </span>
                    </td>
                    <td className="py-4 px-6">
                      <div className="space-y-1">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <p className="font-bold text-slate-800">{user.school || user.orgName || (user.role === 'parent' ? 'Parent / Guardian' : 'N/A')}</p>
                          {user.role === 'organization' && (
                            isUserOrgLead(user, users) ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-amber-500 text-white text-[9px] font-black uppercase tracking-wider">
                                <Crown className="w-2.5 h-2.5" />
                                LEAD
                              </span>
                            ) : (
                              <span className="px-2 py-0.5 rounded bg-slate-100 text-slate-600 text-[9px] font-black uppercase tracking-wider">
                                TEAM MEMBER
                              </span>
                            )
                          )}
                        </div>

                        {/* If Parent: Show FERPA Signed status and when */}
                        {user.role === 'parent' && (() => {
                          const linkedStudents = (users || []).filter(u =>
                            u.role === 'student' && (
                              u.parentId === user.uid ||
                              u.childId === user.uid ||
                              (Array.isArray(u.childrenIds) && u.childrenIds.includes(user.uid)) ||
                              (user.childId && u.uid === user.childId) ||
                              (Array.isArray(user.childrenIds) && user.childrenIds.includes(u.uid)) ||
                              (typeof user.email === 'string' && typeof u.parentEmail === 'string' && u.parentEmail.toLowerCase().trim() === user.email.toLowerCase().trim()) ||
                              (typeof user.email === 'string' && typeof u.ferpaConsentRecipientEmail === 'string' && u.ferpaConsentRecipientEmail.toLowerCase().trim() === user.email.toLowerCase().trim())
                            )
                          );
                          const directGranted = typeof user.ferpaConsent === 'object' ? user.ferpaConsent?.granted === true : user.ferpaConsent === true;
                          const directDate = user.ferpaConsentDate || (typeof user.ferpaConsent === 'object' ? user.ferpaConsent?.date : '');
                          const studentWithConsent = linkedStudents.find(s => 
                            (typeof s.ferpaConsent === 'object' ? s.ferpaConsent?.granted === true : s.ferpaConsent === true) || s.ferpaConsentDate
                          );
                          const isSigned = directGranted || !!studentWithConsent;
                          const consentDate = directDate || (studentWithConsent ? (typeof studentWithConsent.ferpaConsent === 'object' ? studentWithConsent.ferpaConsent?.date : studentWithConsent.ferpaConsentDate) : '');

                          return (
                            <div className="space-y-1 pt-0.5">
                              {isSigned ? (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-800 border border-emerald-300 text-[9px] font-extrabold uppercase tracking-wider shadow-3xs">
                                  <Check className="w-3 h-3 text-emerald-600" />
                                  <span>FERPA Signed: {consentDate ? new Date(consentDate).toLocaleDateString() : 'Active'}</span>
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-amber-50 text-amber-800 border border-amber-300 text-[9px] font-extrabold uppercase tracking-wider shadow-3xs">
                                  <X className="w-3 h-3 text-amber-600" />
                                  <span>FERPA Not Signed</span>
                                </span>
                              )}
                              {linkedStudents.length > 0 && (
                                <p className="text-[10px] text-slate-500 font-medium">
                                  Student: <strong className="text-slate-800">{linkedStudents.map(s => s.name).join(', ')}</strong>
                                </p>
                              )}
                            </div>
                          );
                        })()}

                        {user.role === 'organization' && !isUserOrgLead(user, users) && (
                          <button
                            type="button"
                            disabled={savingUid === user.uid}
                            onClick={async () => {
                              setSavingUid(user.uid);
                              try {
                                await setUserAsOrgLead(user.uid, true);
                              } finally {
                                setSavingUid(null);
                              }
                            }}
                            className="text-[9px] font-black uppercase tracking-wider text-amber-700 hover:underline cursor-pointer"
                          >
                            Make Organization Lead
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <div className="flex flex-wrap gap-1">
                        {[user.tenantId, ...(user.extendedRegions || [])].filter(Boolean).map((region, index) => (
                          <span key={`${region}-${index}`} className="px-2 py-0.5 bg-slate-100 rounded text-[9px] font-bold text-slate-600">
                            {(region || '').toUpperCase()}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="py-4 px-6 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {user.uid !== activeUser?.uid && (
                          <button
                            type="button"
                            onClick={() => startProxySession(user)}
                            className="inline-flex items-center space-x-1.5 px-2.5 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-wider bg-amber-500/10 text-amber-700 dark:text-amber-300 border border-amber-500/30 hover:bg-amber-500/20 hover:border-amber-500/50 transition-all cursor-pointer shadow-3xs"
                            title={`Discreetly mimic ${user.name || user.email}'s view as a remote proxy`}
                          >
                            <Eye className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" />
                            <span>Mimic View</span>
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => setSelectedProfileUser(user)}
                          className="inline-flex items-center space-x-1.5 px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-wider bg-slate-100 text-slate-700 border border-slate-200 hover:bg-slate-200 transition-all cursor-pointer"
                          title="Manage user profile & regional access"
                        >
                          <Edit2 className="w-3.5 h-3.5 text-slate-500" />
                          <span>Edit Profile</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => setSelectedPasswordUser(user)}
                          className="inline-flex items-center space-x-1.5 px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-wider bg-[#0094c8]/10 text-[#0094c8] border border-[#0094c8]/25 hover:bg-[#0094c8]/20 transition-all cursor-pointer"
                          title="Password management"
                        >
                          <Key className="w-3.5 h-3.5" />
                          <span>Reset Password</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {selectedProfileUser && (
        <UserProfileModal
          user={selectedProfileUser}
          isOpen={!!selectedProfileUser}
          onClose={() => setSelectedProfileUser(null)}
          onUpdateUser={async (uid, updates) => {
            await updateUserProfile(uid, updates);
            if (selectedProfileUser && selectedProfileUser.uid === uid) {
              setSelectedProfileUser({ ...selectedProfileUser, ...updates });
            }
          }}
          onResetPassword={(u) => setSelectedPasswordUser(u)}
          onAdminOverrideFerpa={() => {}}
          onResendFerpa={async () => {}}
          onReconcileStudent={async () => {}}
          onSelectAnotherUser={(u) => setSelectedProfileUser(u)}
          allSchools={schools || []}
          allDistricts={[]}
          allUsers={users || []}
          tenants={tenants || []}
        />
      )}

      {selectedPasswordUser && (
        <PasswordManagementModal
          user={selectedPasswordUser}
          isOpen={!!selectedPasswordUser}
          onClose={() => setSelectedPasswordUser(null)}
        />
      )}
    </div>
  );
};
